import { useState } from 'react'
import { AlertTriangle, CheckCircle, XCircle, RefreshCw } from 'lucide-react'
import { useBuilder } from '../../context/BuilderContext'

const SPAM_WORDS = [
  'free', 'winner', 'congratulations', 'urgent', 'act now', 'limited time',
  'buy now', 'order now', 'click here', 'click below', 'no obligation',
  'risk free', 'risk-free', 'guarantee', 'guaranteed', 'no cost', 'no fee',
  'cash', 'money back', 'earn money', 'make money', 'extra income',
  'double your', 'million', 'billion', 'discount', 'lowest price',
  'special promotion', 'one time', 'once in a lifetime', 'exclusive deal',
  'apply now', 'sign up free', 'no credit card', 'no purchase necessary',
  'as seen on', 'dear friend', 'you have been selected', 'you are a winner',
  'claim your', 'collect your', 'increase your', '100% free', '100% satisfied',
  'additional income', 'be your own boss', 'call now', 'do it today',
  'don\'t delete', 'don\'t miss out', 'get it now', 'great offer',
  'incredible deal', 'info you requested', 'instant', 'limited offer',
  'no strings attached', 'offer expires', 'open immediately', 'please read',
  'promise you', 'removes wrinkles', 'satisfaction guaranteed', 'save big',
  'supplies are limited', 'take action', 'this is not spam', 'undisclosed',
  'what are you waiting for', 'while supplies last',
  'you won\'t believe', 'your account'
]

function analyzeSpam(blocks, subject) {
  const issues = []
  let score = 0

  // Gather all text content
  let allText = (subject || '') + ' '
  blocks.forEach(block => {
    const s = block.settings
    if (s.content) allText += s.content.replace(/<[^>]*>/g, '') + ' '
    if (s.text) allText += s.text + ' '
    if (s.htmlContent) allText += s.htmlContent.replace(/<[^>]*>/g, '') + ' '
  })

  const lowerText = allText.toLowerCase()

  // Check spam trigger words
  const foundSpamWords = []
  SPAM_WORDS.forEach(word => {
    if (lowerText.includes(word.toLowerCase())) {
      foundSpamWords.push(word)
    }
  })

  if (foundSpamWords.length > 0) {
    score += Math.min(foundSpamWords.length * 5, 30)
    issues.push({
      type: 'warning',
      message: `Found ${foundSpamWords.length} spam trigger word(s): ${foundSpamWords.slice(0, 5).join(', ')}${foundSpamWords.length > 5 ? '...' : ''}`,
      impact: foundSpamWords.length > 3 ? 'high' : 'medium'
    })
  }

  // Check ALL CAPS
  const capsRegex = /[A-Z]{5,}/g
  const capsMatches = allText.match(capsRegex) || []
  if (capsMatches.length > 0) {
    score += capsMatches.length * 3
    issues.push({
      type: 'warning',
      message: `${capsMatches.length} instance(s) of ALL CAPS text detected`,
      impact: capsMatches.length > 2 ? 'high' : 'medium'
    })
  }

  // Check excessive punctuation
  const exclamations = (allText.match(/!/g) || []).length
  if (exclamations > 3) {
    score += (exclamations - 3) * 2
    issues.push({
      type: 'warning',
      message: `Excessive exclamation marks (${exclamations} found)`,
      impact: exclamations > 6 ? 'high' : 'low'
    })
  }

  const questionMarks = (allText.match(/\?{2,}/g) || []).length
  if (questionMarks > 0) {
    score += questionMarks * 3
    issues.push({
      type: 'warning',
      message: 'Multiple consecutive question marks detected',
      impact: 'low'
    })
  }

  // Check subject line
  if (subject) {
    if (subject === subject.toUpperCase() && subject.length > 5) {
      score += 10
      issues.push({ type: 'error', message: 'Subject line is ALL CAPS', impact: 'high' })
    }
    if (subject.includes('RE:') || subject.includes('FW:')) {
      score += 5
      issues.push({ type: 'warning', message: 'Subject contains RE: or FW: which can appear deceptive', impact: 'medium' })
    }
    if (subject.length > 60) {
      score += 3
      issues.push({ type: 'info', message: `Subject is ${subject.length} chars (recommended: under 60)`, impact: 'low' })
    }
  } else {
    score += 5
    issues.push({ type: 'error', message: 'No subject line set', impact: 'medium' })
  }

  // Check image-to-text ratio
  const imageCount = blocks.filter(b => b.type === 'image' && b.settings.src).length
  const textBlocks = blocks.filter(b => b.type === 'text' || b.type === 'heading').length
  if (imageCount > 0 && textBlocks === 0) {
    score += 15
    issues.push({ type: 'error', message: 'Image-only email with no text content', impact: 'high' })
  } else if (imageCount > textBlocks * 2) {
    score += 8
    issues.push({ type: 'warning', message: 'High image-to-text ratio — add more text', impact: 'medium' })
  }

  // Check for unsubscribe link (required by CAN-SPAM / GDPR)
  if (lowerText.includes('unsubscribe')) {
    score = Math.max(score - 5, 0)
    issues.push({ type: 'pass', message: 'Unsubscribe link found (CAN-SPAM compliant)', impact: 'positive' })
  } else {
    score += 10
    issues.push({ type: 'error', message: 'No unsubscribe link found (required by CAN-SPAM & GDPR)', impact: 'high' })
  }

  // Good practices (reduce score)
  if (blocks.length >= 3) {
    issues.push({ type: 'pass', message: 'Good content structure with multiple blocks', impact: 'positive' })
  }
  if (foundSpamWords.length === 0) {
    issues.push({ type: 'pass', message: 'No spam trigger words detected', impact: 'positive' })
  }

  // Clamp score
  score = Math.min(Math.max(score, 0), 100)

  return { score, issues }
}

export default function SpamChecker() {
  const { blocks, subject } = useBuilder()
  const [result, setResult] = useState(null)

  const runCheck = () => {
    setResult(analyzeSpam(blocks, subject))
  }

  const getScoreColor = (score) => {
    if (score <= 20) return '#22c55e'
    if (score <= 50) return '#f59e0b'
    return '#dc2626'
  }

  const getScoreLabel = (score) => {
    if (score <= 20) return 'Excellent'
    if (score <= 50) return 'Needs Improvement'
    return 'High Risk'
  }

  const getImpactIcon = (type) => {
    switch (type) {
      case 'error': return <XCircle size={14} style={{ color: '#dc2626', flexShrink: 0 }} />
      case 'warning': return <AlertTriangle size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
      case 'pass': return <CheckCircle size={14} style={{ color: '#22c55e', flexShrink: 0 }} />
      default: return <AlertTriangle size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>Spam Score Checker</h4>
      <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
        Analyze your email for common spam triggers
      </p>

      <button
        onClick={runCheck}
        disabled={blocks.length === 0}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
          padding: '0.6rem 1rem', border: '1px solid var(--color-primary)',
          borderRadius: '8px', background: 'rgba(99,102,241,0.05)',
          color: 'var(--color-primary)', cursor: 'pointer', fontSize: '0.85rem',
          fontWeight: '600', opacity: blocks.length === 0 ? 0.5 : 1
        }}
      >
        <RefreshCw size={15} /> Analyze Email
      </button>

      {result && (
        <>
          {/* Score */}
          <div style={{
            textAlign: 'center', padding: '1rem',
            background: `${getScoreColor(result.score)}08`,
            border: `1px solid ${getScoreColor(result.score)}30`,
            borderRadius: '10px'
          }}>
            <div style={{
              fontSize: '2rem', fontWeight: '800',
              color: getScoreColor(result.score),
              lineHeight: 1
            }}>
              {result.score}
            </div>
            <div style={{
              fontSize: '0.7rem', fontWeight: '600',
              color: getScoreColor(result.score),
              marginTop: '0.25rem', textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              {getScoreLabel(result.score)}
            </div>
            <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '0.25rem' }}>
              Lower score = better deliverability
            </div>
          </div>

          {/* Issues */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {result.issues.map((issue, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                padding: '0.5rem 0.6rem', borderRadius: '6px',
                background: issue.type === 'error' ? '#fef2f2' : issue.type === 'warning' ? '#fffbeb' : issue.type === 'pass' ? '#f0fdf4' : '#f8fafc',
                fontSize: '0.75rem', color: '#475569', lineHeight: '1.4'
              }}>
                {getImpactIcon(issue.type)}
                <span>{issue.message}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
